import { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentApplicationSpanish = () => {
  const [formData, setFormData] = useState({
    student_name: '',
    student_email: '',
    student_phone: '',
    grade_level: '',
    school_name: '',
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    preferred_language: '',
    gpa: '',
    sat_score: '',
    college_aspirations: '',
    intended_major: '',
    first_generation: '',
    financial_aid_needed: '',
    current_challenges: '',
    extracurriculars: '',
    how_did_you_hear: '',
    why_lantern_prep: ''
  });
  
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const applicationData = {
        name: formData.student_name,
        email: formData.student_email,
        phone: formData.student_phone,
        inquiry_type: 'student',
        message: JSON.stringify(formData, null, 2)
      };
      
      await axios.post(`${API}/inquiries`, applicationData);
      toast.success('¡Solicitud enviada exitosamente! Nos pondremos en contacto pronto.');
      
      setFormData({
        student_name: '',
        student_email: '',
        student_phone: '',
        grade_level: '',
        school_name: '',
        parent_name: '',
        parent_email: '',
        parent_phone: '',
        preferred_language: '',
        gpa: '',
        sat_score: '',
        college_aspirations: '',
        intended_major: '',
        first_generation: '',
        financial_aid_needed: '',
        current_challenges: '',
        extracurriculars: '',
        how_did_you_hear: '',
        why_lantern_prep: ''
      });
      
      window.scrollTo(0, 0);
    } catch (error) {
      toast.error('Algo salió mal. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div data-testid="student-application-spanish-page">
      <Navbar />
      
      {/* Hero Banner */}
      <section className="bg-primary text-primary-foreground pt-32 pb-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="text-sm font-medium tracking-[0.2em] uppercase text-accent mb-6">
            Solicitud de Beca
          </div>
          <h1 className="text-5xl md:text-6xl font-heading font-semibold tracking-tight leading-[1.05] mb-8">
            Solicita una plaza de beca.
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-primary-foreground/85 max-w-2xl mb-6">
            Lantern Prep otorga un número limitado de plazas de beca completamente financiadas cada ciclo a estudiantes de los condados de Pinellas y Hillsborough que cumplan con uno o más de los siguientes criterios:
          </p>
          <ul className="list-disc pl-6 space-y-2 marker:text-accent text-base md:text-lg leading-relaxed text-primary-foreground/85 max-w-2xl mb-6">
            <li>Primera generación universitaria (ningún padre o madre ha completado una carrera universitaria de cuatro años)</li>
            <li>Familia con necesidad económica demostrada</li>
            <li>Estudiantes que enfrentan circunstancias personales o académicas significativas</li>
          </ul>
          <p className="text-lg md:text-xl leading-relaxed text-primary-foreground/85 max-w-2xl mb-4">
            Los estudiantes seleccionados para becas reciben el programa completo de Lantern Prep sin costo. Cada plaza — pagada o de beca — representa el mismo compromiso de tiempo, experiencia y recursos.
          </p>
          <p className="text-lg md:text-xl leading-relaxed text-primary-foreground/85 max-w-2xl mb-8">
            La selección se basa en mérito, motivación y circunstancia. La solicitud es detallada por diseño — leemos cada envío cuidadosamente.
          </p>
          <div className="flex gap-4 items-center text-sm">
            <a href="/apply" className="text-primary-foreground/70 hover:text-accent transition-colors tracking-wide">
              EN
            </a>
            <span className="text-primary-foreground/30">|</span>
            <span className="text-accent font-medium tracking-wide">ES</span>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 md:py-24 bg-background">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl">
          <form onSubmit={handleSubmit} data-testid="scholarship-application-form-es" className="bg-card border border-border p-8 md:p-12">
            <div className="space-y-8">
              
              {/* Student Information */}
              <div>
                <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-accent mb-6">
                  Información del Estudiante
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="student_name" className="text-foreground font-medium mb-2 block">
                      Nombre Completo *
                    </Label>
                    <Input
                      id="student_name"
                      name="student_name"
                      type="text"
                      required
                      value={formData.student_name}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="Nombre completo del estudiante"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="student_email" className="text-foreground font-medium mb-2 block">
                      Correo Electrónico *
                    </Label>
                    <Input
                      id="student_email"
                      name="student_email"
                      type="email"
                      required
                      value={formData.student_email}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="estudiante@email.com"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="student_phone" className="text-foreground font-medium mb-2 block">
                      Número de Teléfono *
                    </Label>
                    <Input
                      id="student_phone"
                      name="student_phone"
                      type="tel"
                      required
                      value={formData.student_phone}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="(813) 555-1234"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="grade_level" className="text-foreground font-medium mb-2 block">
                      Grado Actual *
                    </Label>
                    <Select
                      required
                      value={formData.grade_level}
                      onValueChange={(value) => setFormData({ ...formData, grade_level: value })}
                    >
                      <SelectTrigger className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm">
                        <SelectValue placeholder="Seleccionar grado" />
                      </SelectTrigger>
                      <SelectContent className="bg-background">
                        <SelectItem value="9th">9º Grado</SelectItem>
                        <SelectItem value="10th">10º Grado</SelectItem>
                        <SelectItem value="11th">11º Grado</SelectItem>
                        <SelectItem value="12th">12º Grado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="school_name" className="text-foreground font-medium mb-2 block">
                      Nombre de la Escuela Secundaria *
                    </Label>
                    <Input
                      id="school_name"
                      name="school_name"
                      type="text"
                      required
                      value={formData.school_name}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="Tu escuela secundaria"
                    />
                  </div>
                </div>
              </div>

              {/* Parent/Guardian Information */}
              <div>
                <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-accent mb-6">
                  Información del Padre/Tutor
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="parent_name" className="text-foreground font-medium mb-2 block">
                      Nombre del Padre/Tutor *
                    </Label>
                    <Input
                      id="parent_name"
                      name="parent_name"
                      type="text"
                      required
                      value={formData.parent_name}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="Nombre completo del padre/tutor"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="parent_email" className="text-foreground font-medium mb-2 block">
                      Correo del Padre/Tutor *
                    </Label>
                    <Input
                      id="parent_email"
                      name="parent_email"
                      type="email"
                      required
                      value={formData.parent_email}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="padre@email.com"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="parent_phone" className="text-foreground font-medium mb-2 block">
                      Teléfono del Padre/Tutor *
                    </Label>
                    <Input
                      id="parent_phone"
                      name="parent_phone"
                      type="tel"
                      required
                      value={formData.parent_phone}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="(813) 555-1234"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="preferred_language" className="text-foreground font-medium mb-2 block">
                      Idioma Preferido *
                    </Label>
                    <Select
                      required
                      value={formData.preferred_language}
                      onValueChange={(value) => setFormData({ ...formData, preferred_language: value })}
                    >
                      <SelectTrigger className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm">
                        <SelectValue placeholder="Seleccionar idioma" />
                      </SelectTrigger>
                      <SelectContent className="bg-background">
                        <SelectItem value="english">Inglés / English</SelectItem>
                        <SelectItem value="spanish">Español</SelectItem>
                        <SelectItem value="both">Ambos / Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Academic Background */}
              <div>
                <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-accent mb-6">
                  Información Académica
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="gpa" className="text-foreground font-medium mb-2 block">
                      GPA Actual (si se conoce)
                    </Label>
                    <Input
                      id="gpa"
                      name="gpa"
                      type="text"
                      value={formData.gpa}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="3.5"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="sat_score" className="text-foreground font-medium mb-2 block">
                      Puntaje SAT (si se tomó)
                    </Label>
                    <Input
                      id="sat_score"
                      name="sat_score"
                      type="text"
                      value={formData.sat_score}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="1200"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="college_aspirations" className="text-foreground font-medium mb-2 block">
                      Aspiraciones Universitarias *
                    </Label>
                    <Textarea
                      id="college_aspirations"
                      name="college_aspirations"
                      required
                      value={formData.college_aspirations}
                      onChange={handleChange}
                      rows={3}
                      placeholder="¿Qué universidades te interesan? ¿Cuáles son tus metas?"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="intended_major" className="text-foreground font-medium mb-2 block">
                      Carrera o Campo de Interés
                    </Label>
                    <Input
                      id="intended_major"
                      name="intended_major"
                      type="text"
                      value={formData.intended_major}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="ej., Ingeniería, Pre-Medicina, Negocios, etc."
                    />
                  </div>
                </div>
              </div>

              {/* Background & Fit */}
              <div>
                <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-accent mb-6">
                  Antecedentes y Adecuación al Programa
                </h2>
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="first_generation" className="text-foreground font-medium mb-2 block">
                      ¿Eres estudiante de primera generación universitaria? *
                    </Label>
                    <Select
                      required
                      value={formData.first_generation}
                      onValueChange={(value) => setFormData({ ...formData, first_generation: value })}
                    >
                      <SelectTrigger className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm">
                        <SelectValue placeholder="Seleccionar una opción" />
                      </SelectTrigger>
                      <SelectContent className="bg-background">
                        <SelectItem value="yes">Sí - Ninguno de mis padres tiene título universitario de 4 años</SelectItem>
                        <SelectItem value="no">No - Uno o ambos padres tienen título universitario de 4 años</SelectItem>
                        <SelectItem value="unsure">No estoy seguro/a</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="financial_aid_needed" className="text-foreground font-medium mb-2 block">
                      ¿Necesitarás ayuda financiera o becas para la universidad? *
                    </Label>
                    <Select
                      required
                      value={formData.financial_aid_needed}
                      onValueChange={(value) => setFormData({ ...formData, financial_aid_needed: value })}
                    >
                      <SelectTrigger className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm">
                        <SelectValue placeholder="Seleccionar una opción" />
                      </SelectTrigger>
                      <SelectContent className="bg-background">
                        <SelectItem value="yes">Sí</SelectItem>
                        <SelectItem value="maybe">Tal vez</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="current_challenges" className="text-foreground font-medium mb-2 block">
                      ¿Cuáles son tus mayores desafíos al prepararte para la universidad? *
                    </Label>
                    <Textarea
                      id="current_challenges"
                      name="current_challenges"
                      required
                      value={formData.current_challenges}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Cuéntanos qué te parece abrumador o confuso sobre el proceso universitario..."
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="extracurriculars" className="text-foreground font-medium mb-2 block">
                      Actividades Extracurriculares, Clubes o Liderazgo
                    </Label>
                    <Textarea
                      id="extracurriculars"
                      name="extracurriculars"
                      value={formData.extracurriculars}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Deportes, clubes, trabajo voluntario, empleos, responsabilidades familiares, etc."
                    />
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-accent mb-6">
                  Información Adicional
                </h2>
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="how_did_you_hear" className="text-foreground font-medium mb-2 block">
                      ¿Cómo te enteraste de Lantern Prep?
                    </Label>
                    <Input
                      id="how_did_you_hear"
                      name="how_did_you_hear"
                      type="text"
                      value={formData.how_did_you_hear}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="Consejero escolar, amigo, redes sociales, etc."
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="why_lantern_prep" className="text-foreground font-medium mb-2 block">
                      ¿Por qué estás interesado/a en Lantern Prep? *
                    </Label>
                    <Textarea
                      id="why_lantern_prep"
                      name="why_lantern_prep"
                      required
                      value={formData.why_lantern_prep}
                      onChange={handleChange}
                      rows={4}
                      placeholder="¿Qué te atrae de nuestro programa? ¿Qué esperas obtener?"
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="pt-6 border-t border-border">
                <button
                  type="submit"
                  data-testid="scholarship-submit-es"
                  disabled={loading}
                  className="w-full md:w-auto bg-primary text-primary-foreground px-12 py-4 rounded-sm font-medium tracking-wide hover:bg-accent hover:text-accent-foreground transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Enviando...' : 'Enviar Solicitud'}
                </button>
                
                <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
                  Lantern Prep proporciona consultoría educativa y apoyo de defensa. Nada en este sitio constituye asesoramiento legal ni crea una relación abogado–cliente.
                </p>
              </div>
            </div>
          </form>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default StudentApplicationSpanish;
